/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
import { useState, useMemo, useEffect } from 'react';
import type { Theme } from '@mui/material/styles';
import clsx from 'clsx';

import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import makeStyles from '@mui/styles/makeStyles';
import InputAdornment from '@mui/material/InputAdornment';
import useAutocomplete from '@mui/material/useAutocomplete';

import IconButton from '@design-system/IconButton';
import IconChevronDown from '@experimental-components/IconsComponents/ChevronDown';

import { useDebounce } from 'hooks/useDebounce';

import ActionItem from './ActionItem';
import InputTextField from './InputTextField';

const useStyles = makeStyles((theme: Theme) => ({
  popupIndicatorOpen: {
    transform: 'rotate(180deg)',
  },

  popper: {
    zIndex: theme.zIndex.modal,
  },

  paper: {
    fontSize: 16,
    padding: 0,
    overflow: 'hidden',
    width: '100%',
    marginLeft: 16,
    marginTop: 16,
    boxShadow: '0px 8px 40px rgba(133, 133, 133, 0.2)',
  },

  listbox: {
    listStyle: 'none',
    margin: 0,
    padding: '16px 0',
    maxHeight: '40vh',
    overflow: 'auto',
  },

  option: {
    minHeight: 48,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    cursor: 'pointer',
    padding: 16,
    boxSizing: 'border-box',
    outline: '0',
    WebkitTapHighlightColor: 'transparent',
    fontSize: 14,

    [theme.breakpoints.up('sm')]: {
      minHeight: 'auto',
    },

    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.darkGrey[5],
    },

    '&:active': {
      backgroundColor: theme.palette.darkGrey[5],
    },

    '&:hover': {
      backgroundColor: theme.palette.darkGrey[5],
    },

    '&[aria-disabled="true"]': {
      opacity: theme.palette.action.disabledOpacity,
      pointerEvents: 'none',
    },
  },

  noOptions: {
    color: theme.palette.text.secondary,
    padding: 16,
    // Match the option rows' font size (14) — without this the empty/no-results
    // text inherits the paper's 16px and reads visibly larger than the listed options.
    fontSize: 14,
  },

  groupLabel: {
    backgroundColor: theme.palette.background.paper,
    top: -8,
  },

  groupUl: {
    padding: 0,
    '& $option': {
      paddingLeft: 24,
    },
  },
  disabled: {
    backgroundColor: theme.palette.darkGrey[5],
  },
}));

type OptionsType = {
  name: string;
  uuid: string;
};

interface UseCustomAutocompleteProps {
  renderOption?: (value: any) => any;
  getOptionLabel: (value: any) => any;
  options: Array<OptionsType>;
  value?: OptionsType | OptionsType[] | null;
  multiple?: boolean;
  onChange?: (event: any, value: any) => any;
  renderTags?: () => void;
  inputValue?: string;
}

interface Props extends UseCustomAutocompleteProps {
  label?: string;
  placeholder?: string;
  isDisabled: boolean;
  noOptionsText?: string;
  showAddOption?: boolean;
  onAction?: () => void;
  disableCloseOnSelect?: boolean;
  id?: string;
  disabled?: boolean;
  onInputChange?: (event: any, value: string, reason: string) => void;
}

function getName(option: OptionsType | OptionsType[] | null) {
  if (!option) return '';
  if (!Array.isArray(option)) {
    return option.name;
  }
  return '';
}

function Autocomplete(props: Props) {
  const {
    label,
    placeholder,
    getOptionLabel = (x: any) => x,
    renderOption: renderOptionProp,
    isDisabled,
    noOptionsText,
    options,
    value,
    multiple,
    onChange,
    onAction = () => {},
    showAddOption = false,
    disableCloseOnSelect = false,
    id = 'parrot-autocomplete',
    disabled = false,
    onInputChange,
  } = props;

  const classes = useStyles();
  const { startDebounce } = useDebounce(600);

  const [keepPopperOpen, setKeepPopperOpen] = useState<boolean>(false);
  const [keepActionItem, setKeepActionItem] = useState<boolean>(false);

  // When onInputChange is provided, disable automatic filtering
  // to allow server-side filtering (e.g., for paginated data)
  const shouldDisableAutoFilter = Boolean(onInputChange);

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    popupOpen,
    setAnchorEl,
    anchorEl,
    inputValue,
  } = useAutocomplete({
    id,
    componentName: 'ParrotAutocomplete',
    options,
    getOptionLabel,
    multiple,
    value,
    disableCloseOnSelect,
    onChange,
    onInputChange,
    // Disable automatic filtering when doing server-side search
    filterOptions: shouldDisableAutoFilter ? (x) => x : undefined,
  });
  const renderOption: any = renderOptionProp || getOptionLabel;

  const blockPopper = useMemo(() => {
    if (keepPopperOpen) return true;
    if (groupedOptions.length === 0 && showAddOption && inputValue && !keepPopperOpen) {
      setKeepPopperOpen(true);
      return true;
    }
    return false;
  }, [groupedOptions.length, showAddOption, keepPopperOpen, inputValue]);

  const renderListOption = (option: OptionsType | OptionsType[], index: number) => {
    const optionProps = getOptionProps({ option, index });
    return (
      <li {...optionProps} className={classes.option}>
        {renderOption(option, {
          selected: optionProps['aria-selected'],
          inputValue,
        })}
      </li>
    );
  };

  function handleOnAction() {
    onAction();
    setTimeout(() => setKeepPopperOpen(false), 50);
  }

  function handleClickAway() {
    if (blockPopper) setKeepPopperOpen(false);
  }

  useEffect(() => {
    if (!keepActionItem && groupedOptions.length === 0 && showAddOption && inputValue !== getName(value || null)) {
      setKeepActionItem(true);
    }
  }, [keepActionItem, groupedOptions.length, showAddOption, inputValue, value]);

  // Hide the "Agregar" action item when options become available or match appears
  useEffect(() => {
    if (keepActionItem && groupedOptions.length > 0) {
      setKeepActionItem(false);
    }
  }, [groupedOptions.length, keepActionItem]);

  useEffect(() => {
    if (showAddOption && !keepPopperOpen) {
      setKeepActionItem(false);
    }
    if (showAddOption && !inputValue && keepPopperOpen) {
      startDebounce(() => setKeepActionItem(false));
    }
  }, [keepPopperOpen, inputValue, showAddOption]);

  return (
    <div data-testid={id}>
      <InputTextField
        autInputLabelProps={{ ...getInputLabelProps() }}
        autInputProps={{
          ...getInputProps(),
          inputRef: setAnchorEl,
        }}
        className={isDisabled || disabled ? classes.disabled : ''}
        disabled={disabled}
        endAdornment={
          <InputAdornment position="end">
            <IconButton disabled={isDisabled || disabled} size="large">
              <IconChevronDown
                className={clsx({
                  [classes.popupIndicatorOpen]: popupOpen,
                })}
                color="#000"
              />
            </IconButton>
          </InputAdornment>
        }
        fullWidth
        label={label}
        placeholder={placeholder}
        rootProps={{ ...getRootProps() }}
      />

      {(popupOpen || blockPopper) && anchorEl && !disabled ? (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Popper
            anchorEl={anchorEl}
            className={clsx(classes.popper)}
            data-testid={`presentation-${id}`}
            open
            role="presentation"
            style={{
              width: anchorEl ? anchorEl.clientWidth + 38 : undefined,
            }}
          >
            <Paper className={classes.paper}>
              {keepActionItem ? (
                <ActionItem itemName={inputValue} label="Agregar" onAction={() => handleOnAction()} />
              ) : null}
              {groupedOptions.length === 0 && !showAddOption ? (
                <div className={classes.noOptions}>{noOptionsText}</div>
              ) : null}
              {groupedOptions.length > 0 ? (
                <ul className={classes.listbox} {...getListboxProps()}>
                  {groupedOptions.map((option, index) => renderListOption(option, index))}
                </ul>
              ) : null}
            </Paper>
          </Popper>
        </ClickAwayListener>
      ) : null}
    </div>
  );
}

export default Autocomplete;
